import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import Icon from '@components/Icon/Icon';
import StackedIcon from '@components/Icon/StackedIcon';
import List from '@components/List';
import type { ListItemProps, ListProps } from '@components/List/List';

import { PLAN_FEATURE_MAP, PlanFeature } from '@constants/plans';

import { useNavigate } from '@hooks/useNavigate';

import { formatDate } from '@utils/format';

import type { PlanCreditModel } from 'generated/types';
import { useGetUserPlan } from 'generated/user.generated';

const messages = defineMessages({
  title: {
    id: 'planFeature.title',
    defaultMessage: 'Plan: {name}',
  },
  subtitle: {
    id: 'planFeature.subtitle',
    defaultMessage: 'Effective Date: {date}',
  },
  organization: {
    id: 'planFeature.organization',
    defaultMessage: 'Organization: {name}',
  },
  unlimited: {
    id: 'planFeature.unlimited',
    defaultMessage: 'Unlimited',
  },
  remaining: {
    id: 'planFeature.remaining',
    defaultMessage: '({remaining}/{total} left)',
  },
});

interface PatientPlanFeaturesProps {
  user_id: string;
}

export default function PatientPlanFeatures({
  user_id,
}: PatientPlanFeaturesProps) {
  const { formatMessage } = useIntl();
  const { navigateRoute } = useNavigate();
  const theme = useTheme();

  const { data, loading } = useGetUserPlan({
    variables: {
      input: {
        user_id,
      },
    },
    skip: !user_id,
  });

  const orgName = data?.getUserRecord.parent_account?.account_name;
  const userPlan = data?.getUserRecord.plan;
  const limitMap = (userPlan?.credits ?? []).reduce<
    Record<string, PlanCreditModel>
  >((acc, curr) => {
    acc[curr.id] = curr;
    return acc;
  }, {});

  const features: ListProps['items'] = Object.values(PlanFeature).map((id) => {
    const properties = PLAN_FEATURE_MAP[id];
    const clickable = Boolean(properties.route);

    let limit = formatMessage(messages.unlimited);
    if (id in limitMap) {
      if (limitMap[id].interval === null) {
        limit = formatMessage(messages.unlimited);
      } else {
        limit = formatMessage(
          { id: `planFeature.interval.${limitMap[id].interval}` },
          {
            count: limitMap[id].limit,
          }
        );
      }
    }
    const titleText = clickable ? (
      <Link
        onClick={() => navigateRoute(properties.route ?? '')}
        sx={{ fontSize: 16, textDecoration: 'none' }}
      >
        {formatMessage({ id: `planFeature.item.${id}` })}
      </Link>
    ) : (
      <Typography>{formatMessage({ id: `planFeature.item.${id}` })}</Typography>
    );

    const props: ListItemProps = {
      startingAction: (
        <StackedIcon
          icons={properties.icon.map(({ name }) => ({
            name,
            type: 'fad',
            color: 'primary.main',
          }))}
        />
      ),
      endingAction: (
        <>
          <Typography variant="body1" mr={1}>
            {limit}
          </Typography>
          <Box width={'10px'}>
            {clickable ? <Icon type="fad" name="chevron-right" /> : null}
          </Box>
        </>
      ),
      title: titleText,
      handleClick: clickable
        ? () => {
            navigateRoute(properties.route ?? '');
          }
        : undefined,
    };

    switch (id) {
      /*
      case PlanFeature.UserAccessManagement:
      case PlanFeature.DiagnosticImages:
      case PlanFeature.PrimaryPractitionerRecords:
      case PlanFeature.RelevantPractitionerRecords:
      */
      case PlanFeature.CaregiverUsers:
      case PlanFeature.Dha:
      case PlanFeature.FaceScans:
      case PlanFeature.BodyScans:
      case PlanFeature.CoughScans:
      case PlanFeature.SkinScans:
        // eslint-disable-next-line no-case-declarations
        const remainingText =
          id in limitMap && limitMap[id].limit ? (
            <Typography color="purple.main">
              {formatMessage(messages.remaining, {
                remaining: limitMap[id].remaining,
                total: limitMap[id].limit,
              })}
            </Typography>
          ) : undefined;
        return {
          ...props,
          display: Boolean(id in limitMap),
          title: (
            <Stack direction={{ xs: 'column', sm: 'row' }}>
              {titleText}
              {remainingText}
            </Stack>
          ),
        };
      default:
        return props;
    }
  });

  return (
    <ContentLoader lineCount={3} loading={loading}>
      <Card>
        <CardHeader
          action={formatMessage(messages.organization, {
            name: orgName,
          })}
          subheader={formatMessage(messages.subtitle, {
            date: formatDate({ date: userPlan?.effective_date }),
          })}
          title={formatMessage(messages.title, {
            name: userPlan?.name,
          })}
          titleTypographyProps={{
            color: 'primary.main',
            variant: 'body1',
          }}
        />
        <Divider />

        <CardContent>
          <List
            type="user-plan-features"
            items={features}
            paperProps={{
              boxShadow: 'none',
            }}
            buttonProps={{
              py: 0,
              my: 0.75,
              minHeight: 32,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '5px',
              backgroundColor: theme.palette.divider,
            }}
          />
        </CardContent>
      </Card>
    </ContentLoader>
  );
}
